// Pure validation for CampaignConfig. The configurator page calls this on
// every render to compute inline errors and to decide whether Submit is
// enabled. No side effects, no I/O — safe to reuse in tests.

import { CampaignConfig, OracleDataset } from '../types';

export type ValidationResult =
  | { ok: true }
  | { ok: false; errors: Record<string, string> };

export function validateCampaignConfig(
  config: CampaignConfig,
  oracle?: OracleDataset,
): ValidationResult {
  const errors: Record<string, string> = {};

  // Name
  if (!config.name || config.name.trim().length === 0) {
    errors.name = 'Name is required.';
  }

  // Inputs: at least one range axis
  const rangeAxes = config.inputs.filter((a) => a.mode === 'range');
  if (rangeAxes.length === 0) {
    errors.inputs = 'At least one input must be in range mode.';
  }

  // Inputs: validate per-axis bounds / fixed values
  config.inputs.forEach((axis, idx) => {
    const key = `inputs.${idx}`;
    if (axis.mode === 'range') {
      const hasMin = typeof axis.min === 'number' && Number.isFinite(axis.min);
      const hasMax = typeof axis.max === 'number' && Number.isFinite(axis.max);
      if (!hasMin || !hasMax) {
        errors[key] = `${axis.name}: min and max are required.`;
      } else if ((axis.min as number) >= (axis.max as number)) {
        errors[key] = `${axis.name}: min must be less than max.`;
      }
    } else {
      const hasFixed =
        typeof axis.fixedValue === 'number' && Number.isFinite(axis.fixedValue);
      if (!hasFixed) {
        errors[key] = `${axis.name}: fixed value is required.`;
      }
    }
  });

  // Budget / batch size
  if (!Number.isInteger(config.batchSize) || config.batchSize < 1) {
    errors.batchSize = 'Batch size must be a positive integer.';
  }
  if (!Number.isInteger(config.budget) || config.budget < 1) {
    errors.budget = 'Budget must be a positive integer.';
  }
  if (
    Number.isInteger(config.batchSize) &&
    Number.isInteger(config.budget) &&
    config.budget < config.batchSize
  ) {
    errors.budget = 'Budget must be greater than or equal to batch size.';
  }

  // Outputs
  if (!config.outputs || config.outputs.length === 0) {
    errors.outputs = 'Select at least one output.';
  }

  // Oracle: at least one selected output must be available in the oracle
  if (
    config.oracle.enabled &&
    oracle &&
    config.outputs &&
    config.outputs.length > 0
  ) {
    const availableNames = new Set(
      oracle.outputMappings
        .filter((m) => m.available)
        .map((m) => m.canonicalName),
    );
    const selectedAvailable = config.outputs.filter((o) =>
      availableNames.has(o.name),
    );
    if (selectedAvailable.length === 0) {
      errors.outputs =
        'At least one selected output must be available in the chosen oracle dataset.';
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true };
}
